/**
 * The band photo PLACEMENT model — which of the five photo slots backs which hero surface.
 *
 * This is the CNTRL equivalent of the legacy's `placements.dashboard | content | music`, and it is more
 * explicit: every slot carries an IMPLICIT default placement, and an explicit `epkRole` on any slot overrides
 * it. A surface asks "which photo holds MY placement", never "which slot number do I want" — binding by slot
 * number is what let the dashboard hero and the Assets HERO tag disagree about the same photo.
 *
 * Shared by Assets → Brand (which assigns placements) and the surfaces that consume them
 * (`useSpotlightPhoto`, `useMusicHeroPhoto`, `useMarketingHeroPhoto`, and server-side
 * `ContentReadService`). Keep them reading from HERE — a second copy is a divergence bug waiting to happen.
 *
 * ── THE VOCABULARY IS NAMED FOR PAGES (PO, 2026-09-06; ART-95) ────────────────────────────────────────────
 * A band picks a PAGE, not a design term: "Dashboard Page" / "Socials Page" / "Streaming Page" /
 * "Marketing Page". The previous wording ("Press kit only", "Dashboard headline", "Content spotlight",
 * "Music hero") is retired, and so is "Assets Page" (PO, 2026-09-07): the off state is **"None"**, because
 * naming it after a page put a fifth page in a list of four and read as a placement rather than as the
 * absence of one.
 *
 * **The off state is structural, not a spare label.** Five slots cannot share four placements that are each
 * one-per-band, so one slot always holds none — and `setEpkRole` needs somewhere to demote a photo it
 * displaces. Whatever it is called, a fifth value has to exist and has to be selectable, or a band cannot
 * take a photo out of rotation at all.
 *
 * **`epk_role` is stored FREE TEXT server-side** — `string?`, max 64 chars, no enum and no CHECK — and
 * matching everywhere is by literal string. So renaming a value here is a DATA change, not a copy change:
 * migration `PhotoPlacementsArePages` rewrites every stored value in step. If you rename one again, migrate
 * it in the same commit, or every band that had explicitly assigned it silently loses its hero (nothing
 * errors, nothing logs — `photoForPlacement` simply finds no holder and the surface renders its empty look).
 *
 * **SOCIALS IS ITS OWN PLACEMENT AGAIN (PO, 2026-09-07; ART-95 round two).** It briefly shared "Marketing
 * Page" with the Marketing overview hero, on the reasoning that one band choice should back both. The PO's
 * correction is that Socials is a PAGE like the other three, so it gets an entry: `/content`'s reach hero
 * reads "Socials Page" (`ContentReadService.HeroPlacement`) and only the Marketing overview reads "Marketing
 * Page" (`useMarketingHeroPhoto`). Those are the two readers; they no longer overlap.
 *
 * What made the rename SAFE without a data migration, unlike the round-one rename: "Socials Page" is a value
 * that has never been stored, so no band holds it and slot 4's new default cannot compete with an explicit
 * assignment (the hazard recorded below). Nothing was renamed and nothing was evicted.
 *
 * Bands that explicitly hold "Marketing Page" KEEP it as their Marketing hero, and that is a deliberate
 * choice rather than an oversight. Every such row was written as "Content spotlight" before round one, so
 * arguably it means Socials — but "Marketing Page" has been assignable since, those two origins are now
 * indistinguishable in the column, and rewriting a band's explicit choice on a guess about where it came from
 * is worse than letting a new placement start from its slot default.
 */

/**
 * The picker's options, IN THE ORDER IT SHOWS THEM (PO, 2026-09-07): "None" leads on every row, then the
 * four pages. The picker used to hoist each slot's own default to the top, so the list read differently on
 * all five rows and the off state moved around inside it. Reorder here, never at a call site.
 */
export const EPK_ROLES = ["None", "Dashboard Page", "Socials Page", "Streaming Page", "Marketing Page"] as const;
export type EpkRole = (typeof EPK_ROLES)[number];

/**
 * The five FIXED photo slots. Bind by `slot` key, never by array index.
 *
 * Four page placements plus the off state over five slots, so every slot carries a distinct default and all
 * four heroes are filled out of the box. Alt Photo 1 (slot 4) defaults to Socials Page, which restores what
 * that slot has always meant — its pre-rename default was "Content spotlight", the same surface — after
 * round one briefly folded Socials into Marketing and left slot 4 as a second spare. Main Photo (slot 1) is
 * the one that holds none, and it is the right slot for it: the band's primary press shot belongs in the
 * press kit whether or not it backs a hero.
 */
export const PHOTO_SLOTS: readonly { slot: number; name: string; defaultRole: EpkRole }[] = [
  { slot: 1, name: "Main Photo", defaultRole: "None" },
  { slot: 2, name: "Main Photo 2", defaultRole: "Marketing Page" },
  { slot: 3, name: "Live Photo", defaultRole: "Dashboard Page" },
  { slot: 4, name: "Alt Photo 1", defaultRole: "Socials Page" },
  { slot: 5, name: "Alt Photo 2", defaultRole: "Streaming Page" },
] as const;

/** The off state — a photo that backs no hero and lives in the press kit alone. */
export const NO_PLACEMENT: EpkRole = "None";

export const slotDef = (slot: number | null | undefined) =>
  PHOTO_SLOTS.find((s) => s.slot === slot) ?? PHOTO_SLOTS[0]!;

/**
 * A photo's effective placement: its explicit assignment, else its slot default.
 *
 * `slot` is typed optional because the generated `BandPhotoView` marks every property optional; a row without
 * one falls back to slot 1's default rather than throwing.
 */
export const effectivePlacement = (photo: { slot?: number | null; epkRole?: string | null }): string =>
  photo.epkRole ?? slotDef(photo.slot).defaultRole;

/**
 * The photo holding a placement, or `undefined` when none does. Only FILLED slots can hold one — an empty
 * slot never wears the tag, and never backs a hero.
 *
 * There is deliberately NO fallback to another slot: if no photo holds the placement the surface shows its
 * designed empty look. Inventing a fallback would make a hero display a photo that Assets does not tag as
 * that hero — two surfaces disagreeing about one band's intent.
 */
export function photoForPlacement<T extends { slot?: number | null; epkRole?: string | null }>(
  photos: readonly T[],
  placement: EpkRole,
): T | undefined {
  return photos.find((p) => effectivePlacement(p) === placement);
}
